import moment from "moment";
import { DATE_TIME_FORMAT } from "@lhd/constants";
import type { WorkerLogtimesMonthlyParams } from "@lhd/hooks";

export const normalizeJapaneseDateInput = (raw: string): string =>
  String(raw ?? "")
    .trim()
    .replace(/[\uFF10-\uFF19]/g, (ch) =>
      String.fromCharCode(ch.charCodeAt(0) - 0xfee0),
    )
    .replace(/\u3000/g, " ")
    .replace(/[／－]/g, (ch) => (ch === "／" ? "/" : "-"));

export const MONTHLY_LIST_URL = {
  workerNameCont: "filter_worker_names",
  workerNameContAny: "worker_name_cont_any",
  noteCont: "filter_note",
  noteContAny: "note_cont_any",
  creatorNameCont: "filter_creator_names",
  creatorNameContAny: "creator_name_cont_any",
  companyNameAny: "company_name_any",
  logtimeStatus: "logtime_status",
  sortBy: "sort_by",
  sortOrder: "sort_order",
  firstCheckinGteq: "first_checkin_time_gteq",
  firstCheckinLteq: "first_checkin_time_lteq",
  lastCheckoutGteq: "last_checkout_time_gteq",
  lastCheckoutLteq: "last_checkout_time_lteq",
  createdAtGteq: "created_at_gteq",
  createdAtLteq: "created_at_lteq",
  filterCreatedAt: "filter_created_at",
  filterFirstCheckinTime: "filter_first_checkin_time",
  filterLastCheckoutTime: "filter_last_checkout_time",
  createdAtAny: "created_at_any",
  firstCheckinTimeAny: "first_checkin_time_any",
  lastCheckoutTimeAny: "last_checkout_time_any",
  dateIn: "date_in",
  legacyFirstCheckinGteq: "first_checkin_gteq",
  logtimeStatusAny: "logtime_status_any",
} as const;

export const MONTHLY_LIST_SORT_API_FIELDS = [
  "worker_name",
  "last_checkout_time",
  "first_checkin_time",
  "punch_check_in",
  "punch_check_out",
  "corrected_check_in",
  "corrected_check_out",
  "note",
  "logtime_status",
  "creator_name",
  "created_at",
] as const;

export const monthlyListSortBracketKey = (apiField: string): string =>
  `sort[${apiField}]`;

export const monthlyListFilterBracketKey = (apiField: string): string =>
  `filters[${apiField}]`;

export const monthlyListSearchBracketKey = (apiField: string): string =>
  `search[${apiField}]`;

export const parseBracketFiltersFromSearchParams = (
  sp: URLSearchParams,
): Record<string, string[]> => {
  const out: Record<string, string[]> = {};

  for (const [key, rawValue] of Array.from(sp.entries())) {
    if (!key.startsWith("filters[") || !key.endsWith("]")) continue;

    const field = key.slice(8, -1).trim();
    if (!field) continue;

    let values: string[] = [];
    try {
      const parsed = JSON.parse(rawValue);
      if (Array.isArray(parsed)) {
        values = parsed
          .map((value) => String(value ?? "").trim())
          .filter(Boolean);
      } else {
        values = rawValue.trim() ? [rawValue.trim()] : [];
      }
    } catch {
      values = rawValue
        .split(/[,，]/)
        .map((value) => value.trim())
        .filter(Boolean);
    }

    if (!values.length) continue;
    out[field] = Array.from(new Set([...(out[field] ?? []), ...values]));
  }

  return out;
};

export const parseBracketSearchesFromSearchParams = (
  sp: URLSearchParams,
): Record<string, string> => {
  const out: Record<string, string> = {};

  for (const [key, rawValue] of Array.from(sp.entries())) {
    if (!key.startsWith("search[") || !key.endsWith("]")) continue;

    const field = key.slice(7, -1).trim();
    const value = rawValue.trim();
    if (field && value) out[field] = value;
  }

  return out;
};

export const parseBracketSortsFromSearchParams = (
  sp: URLSearchParams,
): Record<string, "asc" | "desc"> => {
  const out: Record<string, "asc" | "desc"> = {};
  for (const [k, vRaw] of Array.from(sp.entries())) {
    if (!k.startsWith("sort[") || !k.endsWith("]")) continue;
    let inner = k.slice(5, -1);
    const v = vRaw.trim().toLowerCase();
    if (inner && (v === "asc" || v === "desc")) {
      if (inner === "first_checkin_time") {
        inner = "punch_check_in";
      } else if (inner === "last_checkout_time") {
        inner = "punch_check_out";
      }
      out[inner] = v as "asc" | "desc";
    }
  }
  return out;
};

export const MONTHLY_LIST_FILTER_KEYS: string[] = [
  MONTHLY_LIST_URL.workerNameCont,
  MONTHLY_LIST_URL.workerNameContAny,
  MONTHLY_LIST_URL.noteCont,
  MONTHLY_LIST_URL.noteContAny,
  MONTHLY_LIST_URL.creatorNameCont,
  MONTHLY_LIST_URL.creatorNameContAny,
  MONTHLY_LIST_URL.companyNameAny,
  MONTHLY_LIST_URL.logtimeStatus,
  MONTHLY_LIST_URL.logtimeStatusAny,
  MONTHLY_LIST_URL.firstCheckinGteq,
  MONTHLY_LIST_URL.firstCheckinLteq,
  MONTHLY_LIST_URL.lastCheckoutGteq,
  MONTHLY_LIST_URL.lastCheckoutLteq,
  MONTHLY_LIST_URL.createdAtGteq,
  MONTHLY_LIST_URL.createdAtLteq,
  MONTHLY_LIST_URL.filterCreatedAt,
  MONTHLY_LIST_URL.filterFirstCheckinTime,
  MONTHLY_LIST_URL.filterLastCheckoutTime,
  MONTHLY_LIST_URL.createdAtAny,
  MONTHLY_LIST_URL.firstCheckinTimeAny,
  MONTHLY_LIST_URL.lastCheckoutTimeAny,
  MONTHLY_LIST_URL.dateIn,
  MONTHLY_LIST_URL.legacyFirstCheckinGteq,
];

const JP_STATUS_TO_API: Record<string, string> = {
  出勤: "checked_out",
  未終了: "not_checked_out",
  休日: "not_checked_in",
  勤務中: "working",
  checked_out: "checked_out",
  not_checked_out: "not_checked_out",
  not_checked_in: "not_checked_in",
  working: "working",
  on_break: "on_break",
};

export const mapLogtimeStatusFilterToApi = (
  raw: string,
): string | undefined => {
  const s = raw.trim().replace(/\s+/g, "");
  if (!s) return undefined;
  const parts = s
    .split(/[,，]/)
    .map((p) => p.trim())
    .filter(Boolean);
  const mapped = parts.map((p) => JP_STATUS_TO_API[p] ?? p).filter(Boolean);
  return mapped.length ? mapped.join(",") : undefined;
};

const DATE_PARSE_FORMATS = [
  DATE_TIME_FORMAT.DATE_TIME_JP,
  DATE_TIME_FORMAT.DATE_JP_TIME,
  DATE_TIME_FORMAT.DATE_TIME_JP_MD,
  DATE_TIME_FORMAT.DATE_JP_MD_TIME,
  DATE_TIME_FORMAT.DATE_JP,
  DATE_TIME_FORMAT.DATE_JP_MD,
  DATE_TIME_FORMAT.DATE_MONTH_JP,
  DATE_TIME_FORMAT.DATE_MONTH_JP_M,
  DATE_TIME_FORMAT.YEAR_JP,
  "YYYY年MM月DD日 HH",
  "YYYY年M月D日 H",
  "YYYY年MM月DD日HH",
  "YYYY年M月D日H",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD HH:mm",
  "YYYY-MM-DD",
  moment.ISO_8601,
];

export type DateRangeApi = { gteq?: string; lteq?: string };

export const parseDateFilterToRange = (raw: string): DateRangeApi => {
  const s = normalizeJapaneseDateInput(raw);
  if (!s) return {};
  const m = moment(s, DATE_PARSE_FORMATS, true);
  if (!m.isValid()) return {};
  const isYearOnly = /^\d{4}年$/.test(s);
  const isMonthOnly = /^\d{4}年\d{1,2}月$/.test(s);
  const isDayOnly = /^\d{4}年\d{1,2}月\d{1,2}日$/.test(s);
  const isHourOnly = /^\d{4}年\d{1,2}月\d{1,2}日\s?\d{1,2}(時)?$/.test(s);
  const hasMinute =
    /\d{1,2}:\d{2}$/.test(s) || /T\d{2}:\d{2}/.test(s) || m.minutes() !== 0;
  const hasSecond = m.seconds() !== 0;

  if (isYearOnly) {
    return {
      gteq: m.clone().startOf("year").format("YYYY-MM-DD HH:mm:ss"),
      lteq: m.clone().endOf("year").format("YYYY-MM-DD HH:mm:ss"),
    };
  }
  if (isMonthOnly) {
    return {
      gteq: m.clone().startOf("month").format("YYYY-MM-DD HH:mm:ss"),
      lteq: m.clone().endOf("month").format("YYYY-MM-DD HH:mm:ss"),
    };
  }
  if (isDayOnly) {
    return {
      gteq: m.clone().startOf("day").format("YYYY-MM-DD HH:mm:ss"),
      lteq: m.clone().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
    };
  }
  if (isHourOnly) {
    return {
      gteq: m.clone().startOf("hour").format("YYYY-MM-DD HH:mm:ss"),
      lteq: m.clone().endOf("hour").format("YYYY-MM-DD HH:mm:ss"),
    };
  }
  if (hasMinute || hasSecond) {
    return {
      gteq: m.clone().startOf("minute").format("YYYY-MM-DD HH:mm:ss"),
      lteq: m.clone().endOf("minute").format("YYYY-MM-DD HH:mm:ss"),
    };
  }
  return {
    gteq: m.clone().startOf("day").format("YYYY-MM-DD HH:mm:ss"),
    lteq: m.clone().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
  };
};

export const parseCreatedAtFilterLabelToDayRange = (
  raw: string,
): DateRangeApi => {
  const s = normalizeJapaneseDateInput(raw);
  if (!s) return {};
  const m = moment(s, DATE_PARSE_FORMATS, true);
  if (!m.isValid()) return {};
  return {
    gteq: m.clone().startOf("day").format("YYYY-MM-DD HH:mm:ss"),
    lteq: m.clone().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
  };
};

const JP_CAL_DAY = "YYYY年MM月DD日";

export const jpDateTimeStringToJpCalendarDay = (
  raw: string,
): string | undefined => {
  const s = normalizeJapaneseDateInput(raw);
  if (!s) return undefined;
  const looksIsoWithZone =
    /[Tt]\d/.test(s) || /[Zz]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
  if (looksIsoWithZone) {
    const zoned = moment.parseZone(s);
    if (zoned.isValid()) return zoned.format(JP_CAL_DAY);
  }
  const m = moment(s, DATE_PARSE_FORMATS, true);
  if (!m.isValid()) return undefined;
  return m.format(JP_CAL_DAY);
};

export const isoCreatedAtToJpCalendarDay = (iso: string): string | undefined =>
  jpDateTimeStringToJpCalendarDay(String(iso ?? ""));

export const formatApiDateTimeForFilterDisplay = (
  iso?: string | null,
): string => {
  if (!iso?.trim()) return "";
  const m = moment.parseZone(iso.trim(), DATE_PARSE_FORMATS, true);
  return m.isValid() ? m.format(DATE_TIME_FORMAT.DATE_TIME_JP) : iso.trim();
};

export type MonthlyListSearchPatch = Record<string, string | null | undefined>;

export {
  BLANK_FILTER_LABEL,
  NULL_FILTER_TOKENS,
  isNullFilterToken,
  type NullFilterToken,
} from "@lhd/utility";

export const applyMonthlyListPatch = (
  current: URLSearchParams,
  patch: MonthlyListSearchPatch,
): URLSearchParams => {
  const next = new URLSearchParams(current);
  (Object.entries(patch) as [string, string | null | undefined][]).forEach(
    ([k, v]) => {
      if (v == null || String(v).trim() === "") {
        next.delete(k);
      } else {
        next.set(k, String(v).trim());
      }
    },
  );
  return next;
};

export const clearMonthlyListFilters = (
  current: URLSearchParams,
): URLSearchParams => {
  const next = new URLSearchParams(current);
  MONTHLY_LIST_FILTER_KEYS.forEach((k) => next.delete(k));
  const filterBracketKeys = Array.from(next.keys()).filter(
    (k) => k.startsWith("filters[") && k.endsWith("]"),
  );
  filterBracketKeys.forEach((k) => next.delete(k));
  const searchBracketKeys = Array.from(next.keys()).filter(
    (k) => k.startsWith("search[") && k.endsWith("]"),
  );
  searchBracketKeys.forEach((k) => next.delete(k));
  next.delete(MONTHLY_LIST_URL.sortBy);
  next.delete(MONTHLY_LIST_URL.sortOrder);
  const sortBracketKeys = Array.from(next.keys()).filter(
    (k) => k.startsWith("sort[") && k.endsWith("]"),
  );
  sortBracketKeys.forEach((k) => next.delete(k));
  return next;
};

const trimOrU = (v: string | null): string | undefined => {
  const t = v?.trim();
  return t ? t : undefined;
};

export const workerLogtimesMonthlyParamsFromUrl = (
  base: Pick<
    WorkerLogtimesMonthlyParams,
    "year" | "month" | "page" | "perPage" | "workerId"
  >,
  sp: URLSearchParams,
): WorkerLogtimesMonthlyParams => {
  const legacyGteq = trimOrU(sp.get(MONTHLY_LIST_URL.legacyFirstCheckinGteq));
  const fiGteq =
    trimOrU(sp.get(MONTHLY_LIST_URL.firstCheckinGteq)) ?? legacyGteq;
  const fiLteq = trimOrU(sp.get(MONTHLY_LIST_URL.firstCheckinLteq));
  const logSearchRaw = trimOrU(sp.get(MONTHLY_LIST_URL.logtimeStatus));
  const logSearchText = logSearchRaw?.trim() ? logSearchRaw.trim() : undefined;
  const logAnyList = splitCsvParamOrEmpty(
    sp.get(MONTHLY_LIST_URL.logtimeStatusAny),
  );

  const createdAtFilterList = splitCsvParamOrEmpty(
    sp.get(MONTHLY_LIST_URL.createdAtAny),
  );
  const useCreatedAtFilterRow = createdAtFilterList.length > 0;
  const filterCreatedAt = trimOrU(sp.get(MONTHLY_LIST_URL.filterCreatedAt));

  const firstCheckinTimeFilterList = splitCsvParamOrEmpty(
    sp.get(MONTHLY_LIST_URL.firstCheckinTimeAny),
  );
  const lastCheckoutTimeFilterList = splitCsvParamOrEmpty(
    sp.get(MONTHLY_LIST_URL.lastCheckoutTimeAny),
  );
  const useFirstCheckinFilterRow = firstCheckinTimeFilterList.length > 0;
  const useLastCheckoutFilterRow = lastCheckoutTimeFilterList.length > 0;
  const filterFirstCheckinTime = trimOrU(
    sp.get(MONTHLY_LIST_URL.filterFirstCheckinTime),
  );
  const filterLastCheckoutTime = trimOrU(
    sp.get(MONTHLY_LIST_URL.filterLastCheckoutTime),
  );

  const q = {
    workerNameCont:
      trimOrU(sp.get(MONTHLY_LIST_URL.workerNameCont)) ??
      trimOrU(sp.get("worker_name_cont")),
    noteCont:
      trimOrU(sp.get(MONTHLY_LIST_URL.noteCont)) ??
      trimOrU(sp.get("note_cont")),
    creatorNameCont:
      trimOrU(sp.get(MONTHLY_LIST_URL.creatorNameCont)) ??
      trimOrU(sp.get("creator_name_cont")),
    firstCheckinTimeGteq:
      useFirstCheckinFilterRow || filterFirstCheckinTime ? undefined : fiGteq,
    firstCheckinTimeLteq:
      useFirstCheckinFilterRow || filterFirstCheckinTime ? undefined : fiLteq,
    lastCheckoutTimeGteq:
      useLastCheckoutFilterRow || filterLastCheckoutTime
        ? undefined
        : trimOrU(sp.get(MONTHLY_LIST_URL.lastCheckoutGteq)),
    lastCheckoutTimeLteq:
      useLastCheckoutFilterRow || filterLastCheckoutTime
        ? undefined
        : trimOrU(sp.get(MONTHLY_LIST_URL.lastCheckoutLteq)),
    createdAtGteq: useCreatedAtFilterRow
      ? undefined
      : trimOrU(sp.get(MONTHLY_LIST_URL.createdAtGteq)),
    createdAtLteq: useCreatedAtFilterRow
      ? undefined
      : trimOrU(sp.get(MONTHLY_LIST_URL.createdAtLteq)),
    filterCreatedAt,
    filterFirstCheckinTime,
    filterLastCheckoutTime,
    dateIn: useCreatedAtFilterRow
      ? undefined
      : trimOrU(sp.get(MONTHLY_LIST_URL.dateIn)),
  };
  const legacyFilters = {
    worker_names: splitCsvParamOrEmpty(
      sp.get(MONTHLY_LIST_URL.workerNameContAny),
    ),
    company_names: splitCsvParamOrEmpty(
      sp.get(MONTHLY_LIST_URL.companyNameAny),
    ),
    notes: splitCsvParamOrEmpty(sp.get(MONTHLY_LIST_URL.noteContAny)),
    updater_names: splitCsvParamOrEmpty(
      sp.get(MONTHLY_LIST_URL.creatorNameContAny),
    ),
    ...(createdAtFilterList.length ? { updated_at: createdAtFilterList } : {}),
    ...(firstCheckinTimeFilterList.length
      ? { punch_check_in: firstCheckinTimeFilterList }
      : {}),
    ...(lastCheckoutTimeFilterList.length
      ? { punch_check_out: lastCheckoutTimeFilterList }
      : {}),
    ...(logAnyList.length ? { logtime_statuses: logAnyList } : {}),
  };
  const filters = {
    ...legacyFilters,
    ...parseBracketFiltersFromSearchParams(sp),
  };
  const hasQ = Object.values(q).some(
    (v) => v != null && String(v).trim() !== "",
  );
  const hasFilters = Object.values(filters).some((arr) => arr.length > 0);

  let sort = parseBracketSortsFromSearchParams(sp);
  if (Object.keys(sort).length === 0) {
    const legacyBy = trimOrU(sp.get(MONTHLY_LIST_URL.sortBy));
    const legacyOrderRaw = trimOrU(sp.get(MONTHLY_LIST_URL.sortOrder));
    const legacyOrder =
      legacyOrderRaw === "asc" || legacyOrderRaw === "desc"
        ? legacyOrderRaw
        : undefined;
    if (legacyBy && legacyOrder) {
      sort = { [legacyBy]: legacyOrder };
    }
  }

  return {
    ...base,
    ...(Object.keys(sort).length ? { sort } : {}),
    ...(hasQ ? { q } : {}),
    ...(logSearchText ? { filterLogtimeStatuses: logSearchText } : {}),
    ...(hasFilters ? { filters } : {}),
  };
};

const splitCsvParamOrEmpty = (raw: string | null): string[] =>
  (raw ?? "")
    .split(/[,，]/)
    .map((v) => v.trim())
    .filter(Boolean);

export const monthlyListFiltersSignature = (sp: URLSearchParams): string => {
  const base = MONTHLY_LIST_FILTER_KEYS.map(
    (k) => `${k}=${sp.get(k) ?? ""}`,
  ).join("&");
  const sortKeys = Array.from(sp.keys())
    .filter((k) => k.startsWith("sort[") && k.endsWith("]"))
    .sort();
  const sortPart = sortKeys.map((k) => `${k}=${sp.get(k) ?? ""}`).join("&");
  const filterKeys = Array.from(sp.keys())
    .filter((k) => k.startsWith("filters[") && k.endsWith("]"))
    .sort();
  const filterPart = filterKeys.map((k) => `${k}=${sp.get(k) ?? ""}`).join("&");
  const searchKeys = Array.from(sp.keys())
    .filter((k) => k.startsWith("search[") && k.endsWith("]"))
    .sort();
  const searchPart = searchKeys.map((k) => `${k}=${sp.get(k) ?? ""}`).join("&");
  const legacy = `${MONTHLY_LIST_URL.sortBy}=${
    sp.get(MONTHLY_LIST_URL.sortBy) ?? ""
  }&${MONTHLY_LIST_URL.sortOrder}=${sp.get(MONTHLY_LIST_URL.sortOrder) ?? ""}`;
  return [base, filterPart, searchPart, sortPart, legacy]
    .filter(Boolean)
    .join("&");
};
